import React, { useEffect, useState, useRef } from 'react'
import type { AuthUser, AuthSession, AuthError } from '@supabase/supabase-js'
import { usePostHog } from 'posthog-js/react'
import * as Sentry from '@sentry/react'
import { supabase } from '../../lib/supabase'
import { logInfo, logError, logWarn } from '../../utils/logger'
import { trackSignUpConversion } from '../../utils/googleAds'
import { AuthContext } from './AuthContext'
import { ANALYTICS_EVENTS } from '../../constants/analytics'
import {
  sendWelcomeEmailIfPossible,
  shouldSendWelcomeEmailForEmailSignup,
} from '../../utils/welcomeEmail'
import { buildEmailAuthRedirectUrl } from '../../utils/authRedirects'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)
  const authStateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const posthog = usePostHog()

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        logInfo('AuthProvider', 'Initial session loaded', {
          userEmail: session?.user?.email || 'No user',
        })
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      })
      .catch((error) => {
        logWarn('AuthProvider', 'Supabase auth error', { error })
        setLoading(false)
      })

    // Listen for auth changes
    try {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        // Only log auth changes in development to reduce console noise
        if (import.meta.env.DEV) {
          logInfo('AuthProvider', 'Supabase auth event', {
            event,
            userEmail: session?.user?.email || 'No user',
          })
        }

        // Debounce auth state changes to prevent rapid successive updates
        if (authStateTimeoutRef.current) {
          clearTimeout(authStateTimeoutRef.current)
        }

        authStateTimeoutRef.current = setTimeout(() => {
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
        }, 50) // 50ms debounce for auth state changes
      })

      return () => {
        subscription.unsubscribe()
        if (authStateTimeoutRef.current) {
          clearTimeout(authStateTimeoutRef.current)
        }
      }
    } catch (error) {
      logWarn('AuthProvider', 'Supabase auth subscription error', { error })
      setLoading(false)
    }
  }, [])

  // Phase 1 MVP: identify the user in Sentry so User Feedback is prefilled.
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null)
      return
    }

    const username =
      (user.user_metadata?.display_name as string | undefined) ||
      (user.user_metadata?.full_name as string | undefined) ||
      user.email?.split('@')[0]

    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username,
    })
  }, [user])

  useEffect(() => {
    if (!posthog) return

    if (!user) {
      posthog.reset()
      return
    }

    const name =
      (user.user_metadata?.display_name as string | undefined) ||
      (user.user_metadata?.full_name as string | undefined) ||
      user.email?.split('@')[0] ||
      'user'

    const distinctId = posthog.get_distinct_id?.()
    if (distinctId !== user.id) {
      posthog.identify(user.id, { email: user.email ?? undefined, name })
      posthog.setPersonProperties?.({
        email: user.email ?? undefined,
        name,
        supabase_user_id: user.id,
      })
    }
  }, [posthog, user])

  const signIn = async (
    email: string,
    password: string
  ): Promise<{
    data?: { user: AuthUser | null; session: AuthSession | null }
    error: AuthError | null
  }> => {
    try {
      const result = await supabase.auth.signInWithPassword({ email, password })
      logInfo('AuthProvider', 'Sign in result', {
        success: !result.error,
        error: result.error?.message,
      })

      // Track successful login
      if (!result.error && result.data?.session) {
        const eventProps = { method: 'email' }
        posthog?.capture(ANALYTICS_EVENTS.LOG_IN, eventProps)
        if (typeof window.gtag === 'function') {
          window.gtag('event', ANALYTICS_EVENTS.LOG_IN, eventProps)
        }
      }

      return {
        data: {
          user: result.data?.user ?? null,
          session: result.data?.session ?? null,
        },
        error: result.error,
      }
    } catch (err) {
      logError('AuthProvider', 'Sign in error', { error: err })
      return {
        data: {
          user: null,
          session: null,
        },
        error: err as AuthError,
      }
    }
  }

  const signUp = async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<{
    data?: { user: AuthUser | null; session: AuthSession | null }
    error: AuthError | null
  }> => {
    try {
      // Validate email format before sending to Supabase
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        return {
          error: {
            message: 'Please enter a valid email address',
          } as AuthError,
        }
      }

      const result = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || email.split('@')[0], // Use email prefix as fallback
            full_name: displayName || email.split('@')[0],
          },
          emailRedirectTo: buildEmailAuthRedirectUrl({
            currentOrigin: window.location.origin,
            configuredOrigin: import.meta.env.VITE_EMAIL_AUTH_REDIRECT_ORIGIN,
            path: '/board/new',
          }),
        },
      })

      logInfo('AuthProvider', 'Sign up result', {
        success: !result.error,
        error: result.error?.message,
        errorCode: result.error?.status,
        hasSession: !!result.data?.session,
        hasUser: !!result.data?.user,
        identitiesCount: result.data?.user?.identities?.length,
        userEmail: result.data?.user?.email,
      })

      const shouldTreatAsNewEmailSignup = shouldSendWelcomeEmailForEmailSignup(
        result.data?.user
      )

      // Track successful signup (before handling errors)
      if (!result.error && shouldTreatAsNewEmailSignup && result.data?.user) {
        const eventProps = { method: 'email' }
        posthog?.capture(ANALYTICS_EVENTS.SIGN_UP, eventProps)
        if (typeof window.gtag === 'function') {
          window.gtag('event', ANALYTICS_EVENTS.SIGN_UP, eventProps)
        }
        trackSignUpConversion()

        // Send welcome email (fire-and-forget, don't block signup UX)
        // Get session token for welcome email function
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (session?.access_token) {
          void sendWelcomeEmailIfPossible(
            result.data.user,
            session.access_token,
            displayName || email.split('@')[0]
          ).catch((err) => {
            // Defensive: this helper is intended to never throw/reject, but we still
            // guard against regressions so a background failure doesn't surface as
            // an unhandled promise rejection.
            logError('AuthProvider', 'Welcome email fire-and-forget failed', {
              error: err,
              userId: result.data.user?.id,
            })
          })
        }
      }

      // Handle specific Supabase errors
      if (result.error) {
        // Check for various "already exists" error patterns
        const errorMsg = result.error.message.toLowerCase()
        if (
          errorMsg.includes('user already registered') ||
          errorMsg.includes('already been registered') ||
          errorMsg.includes('already exists') ||
          errorMsg.includes('user with this email already exists')
        ) {
          return {
            error: {
              message:
                'An account with this email already exists. Please sign in instead.',
            } as AuthError,
          }
        }
        if (errorMsg.includes('invalid')) {
          return {
            error: {
              message: 'Please enter a valid email address',
            } as AuthError,
          }
        }
        // Return the original error with better logging
        logWarn('AuthProvider', 'Unhandled signup error', {
          message: result.error.message,
          status: result.error.status,
        })
        return { error: result.error }
      }

      // Handle case where user exists but no session returned (unconfirmed user with email confirmation disabled)
      if (result.data?.user && !shouldTreatAsNewEmailSignup) {
        logInfo('AuthProvider', 'User with empty identities detected', {
          email: result.data.user.email,
        })
        return {
          error: {
            message:
              'An account with this email already exists. Please sign in instead.',
          } as AuthError,
        }
      }

      return {
        data: {
          user: result.data?.user ?? null,
          session: result.data?.session ?? null,
        },
        error: null,
      }
    } catch (err) {
      logError('AuthProvider', 'Sign up error', { error: err })
      return {
        error: {
          message: 'An unexpected error occurred during sign up',
        } as AuthError,
      }
    }
  }

  const signOut = async (): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        logError('AuthProvider', 'Error signing out', { error })
        return { error }
      }

      // Clear local state immediately for better UX
      setUser(null)
      setSession(null)

      return { error: null }
    } catch (err) {
      logError('AuthProvider', 'Unexpected error during sign out', {
        error: err,
      })
      return { error: err as AuthError }
    }
  }

  const resetPassword = async (
    email: string
  ): Promise<{ error: AuthError | null }> => {
    try {
      const redirectUrl = buildEmailAuthRedirectUrl({
        currentOrigin: window.location.origin,
        configuredOrigin: import.meta.env.VITE_EMAIL_AUTH_REDIRECT_ORIGIN,
        path: '/reset-password',
      })
      const result = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      })

      logInfo('AuthProvider', 'Password reset result', {
        success: !result.error,
        error: result.error?.message,
      })

      return { error: result.error }
    } catch (err) {
      logError('AuthProvider', '🔐 Password reset error:', err)
      return { error: err as AuthError }
    }
  }

  const signInWithOAuth = async (
    provider: 'google' | 'apple',
    returnTo?: string
  ): Promise<{ error: AuthError | null }> => {
    try {
      const origin = window.location.origin.trim()
      const redirectUrl = `${origin}/auth/callback`

      // Store returnTo in sessionStorage so callback page can use it
      if (returnTo) {
        sessionStorage.setItem('authReturnTo', returnTo)
      } else {
        sessionStorage.removeItem('authReturnTo')
      }

      logInfo('AuthProvider', 'Starting OAuth sign in', {
        provider,
        redirectUrl,
        returnTo,
      })

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
        },
      })

      if (error) {
        logError('AuthProvider', 'OAuth sign in error', { error })
        return { error }
      }

      // Note: On success, browser will redirect to provider, so we won't reach here
      return { error: null }
    } catch (err) {
      logError('AuthProvider', 'OAuth sign in unexpected error', { error: err })
      return { error: err as AuthError }
    }
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
    resetPassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
